import { FilterData, FiltersValues, FilterOption, FilterConfig, FilterConfigItem, FILTER_OPTIONS_TYPE } from './types';
import { cloneDeep } from 'lodash';

// Helper to get field value from data item using config
const getFieldValue = (dataItem: any, configItem: FilterConfigItem): any => {
  return configItem.fieldAccessor ? configItem.fieldAccessor(dataItem) : dataItem[configItem.fieldName];
};

// Helper to get the filter value for a data item, handling static vs dynamic filters
const getFilterValueForItem = (
  dataItem: any,
  configItem: FilterConfigItem
): { value: string; isNotDefined: boolean } => {
  const fieldValue = getFieldValue(dataItem, configItem);
  const isUndefinedOrNull = fieldValue === undefined || fieldValue === null;

  // For dynamic filters, undefined/null becomes 'not_defined'
  // For static filters, the mapping function handles undefined/null
  if (isUndefinedOrNull && configItem.filterOptionsType === FILTER_OPTIONS_TYPE.DYNAMIC) {
    return { value: 'not_defined', isNotDefined: true };
  }

  return { value: configItem.fieldValueToFilterValueMapping(fieldValue), isNotDefined: false };
};

export const getFiltersValues = (data: any[], config: FilterConfig) => {
  const allFiltersValues: FiltersValues = {};
  const initialFiltersValues: FiltersValues = {};
  const actualFiltersValues: FiltersValues = {};

  config.forEach((configItem) => {
    allFiltersValues[configItem.filterData.filterLabel] = [...configItem.allStaticValues];
    initialFiltersValues[configItem.filterData.filterLabel] = [...configItem.initialStaticValues];
    actualFiltersValues[configItem.filterData.filterLabel] = [];
  });

  const pushNotDefined = (filterLabel: string) => {
    allFiltersValues[filterLabel].push('not_defined');
    if (!initialFiltersValues[filterLabel].includes('not_defined')) {
      initialFiltersValues[filterLabel].push('not_defined');
    }
    if (!actualFiltersValues[filterLabel].includes('not_defined')) {
      actualFiltersValues[filterLabel].push('not_defined');
    }
  };

  data.forEach((dataItem) => {
    config.forEach((configItem) => {
      const { value: filterValue, isNotDefined } = getFilterValueForItem(dataItem, configItem);
      const filterLabel = configItem.filterData.filterLabel;

      if (isNotDefined) {
        if (!allFiltersValues[filterLabel].includes('not_defined')) {
          pushNotDefined(filterLabel);
        }
      } else {
        if (!actualFiltersValues[filterLabel].includes(filterValue)) {
          actualFiltersValues[filterLabel].push(filterValue);
        }
        if (configItem.filterOptionsType === FILTER_OPTIONS_TYPE.DYNAMIC) {
          if (!allFiltersValues[filterLabel].includes(filterValue)) {
            allFiltersValues[filterLabel].push(filterValue);
          }
          if (!initialFiltersValues[filterLabel].includes(filterValue)) {
            initialFiltersValues[filterLabel].push(filterValue);
          }
        }
      }
    });
  });

  Object.entries(allFiltersValues).forEach(([filterLabel, filterValues]) => {
    const isDynamic =
      config.find((filter) => filter.filterData.filterLabel === filterLabel)?.filterOptionsType ===
      FILTER_OPTIONS_TYPE.DYNAMIC;
    // Only remove filters that have no values at all, or dynamic filters with only 'not_defined'
    if (
      (isDynamic && filterValues.length === 1 && filterValues[0] === 'not_defined') ||
      filterValues.length === 0 ||
      actualFiltersValues[filterLabel].length < 1
    ) {
      delete allFiltersValues[filterLabel];
      delete initialFiltersValues[filterLabel];
    }
  });

  // For static filters, only include values that actually exist in the data
  // This ensures non-existent options start unchecked
  config.forEach((configItem) => {
    if (configItem.filterOptionsType === FILTER_OPTIONS_TYPE.STATIC) {
      const filterLabel = configItem.filterData.filterLabel;
      const actualValues = actualFiltersValues[filterLabel] || [];
      if (initialFiltersValues[filterLabel]) {
        initialFiltersValues[filterLabel] = initialFiltersValues[filterLabel].filter((value) =>
          actualValues.includes(value)
        );
      }
    }
  });

  const currentFiltersValues = cloneDeep(initialFiltersValues);

  return {
    allFiltersValues,
    initialFiltersValues,
    currentFiltersValues,
  };
};

export const applyFilters = (data: any[], config: FilterConfig, filtersValues: FiltersValues) => {
  return data.filter((dataItem) => {
    return Object.entries(filtersValues).every(([filterLabel, filterValues]) => {
      const configItem = config.find((item) => item.filterData.filterLabel === filterLabel);

      // If filter label doesn't exist in config, skip it (don't apply this filter)
      if (!configItem) return true;
      // If no filter values selected, skip this filter (show all items for this filter)
      if (!filterValues?.length) return true;

      const { value: filterValue } = getFilterValueForItem(dataItem, configItem);
      return filterValues.includes(filterValue);
    });
  });
};

export const getFiltersData = (
  data: any[],
  filteredData: any[],
  config: FilterConfig,
  currentFiltersValues: FiltersValues,
  allFiltersValues: FiltersValues
) => {
  // Compute which values actually exist in the raw (unfiltered) data
  const existingValuesInData: { [filterLabel: string]: Set<string> } = {};
  data.forEach((dataItem) => {
    config.forEach((configItem) => {
      const filterLabel = configItem.filterData.filterLabel;
      if (!existingValuesInData[filterLabel]) {
        existingValuesInData[filterLabel] = new Set();
      }
      const { value: filterValue } = getFilterValueForItem(dataItem, configItem);
      existingValuesInData[filterLabel].add(filterValue);
    });
  });

  const getFilterOptions = (configItem: FilterConfigItem) => {
    if (configItem.filterOptionsType === FILTER_OPTIONS_TYPE.DYNAMIC) {
      return allFiltersValues[configItem.filterData.filterLabel]?.map((value: string) => ({
        id: value,
        label: value === 'not_defined' ? 'Not Defined' : value,
        realCounter: 0,
        futureCounter: 0,
      }));
    } else {
      const newFilterOptions = configItem.filterData.filterOptions.map((option) => ({
        ...option,
        realCounter: 0,
        futureCounter: 0,
      }));
      if (allFiltersValues[configItem.filterData.filterLabel]?.includes('not_defined')) {
        newFilterOptions.push({
          id: 'not_defined',
          label: 'Not Defined',
          realCounter: 0,
          futureCounter: 0,
        });
      }
      return newFilterOptions;
    }
  };

  let result: FilterData[] = config.map((configItem) => ({
    ...configItem.filterData,
    filterOptions: getFilterOptions(configItem),
  }));

  result = result.filter((filter) => allFiltersValues[filter.filterLabel]);

  const incrementCounter = (filterLabel: string, optionId: string) => {
    const filter = result.find((filter) => filter.filterLabel === filterLabel);
    if (!filter) return;
    const option = filter.filterOptions.find((option: FilterOption) => option.id === optionId);
    if (option && option.realCounter !== undefined && option.futureCounter !== undefined) {
      option.realCounter++;
      option.futureCounter++;
    }
  };

  filteredData.forEach((dataItem) => {
    config.forEach((configItem) => {
      const { value: filterValue } = getFilterValueForItem(dataItem, configItem);
      incrementCounter(configItem.filterData.filterLabel, filterValue);
    });
  });

  Object.entries(allFiltersValues).forEach(([filterLabel, filterValues]) => {
    const current = currentFiltersValues[filterLabel] || [];
    filterValues.forEach((value) => {
      if (!current.includes(value)) {
        const clone = cloneDeep(currentFiltersValues);
        // Ensure the filter key exists in clone before pushing
        if (!clone[filterLabel]) {
          clone[filterLabel] = [];
        }
        clone[filterLabel].push(value);
        const newFilteredData = applyFilters(data, config, clone);
        const filter = result.find((item) => item.filterLabel === filterLabel);
        if (filter) {
          const option = filter.filterOptions.find((item) => item.id === value);
          if (option) {
            option.futureCounter = newFilteredData.length - filteredData.length;
          }
        }
      }
    });
  });

  return result.map((filter) => {
    const selectedValues = currentFiltersValues[filter.filterLabel] || [];
    const isLastSelectedOption = (optionId: string) =>
      selectedValues.includes(optionId) && selectedValues.length === 1;
    const existingValues = existingValuesInData[filter.filterLabel] || new Set();

    return {
      ...filter,
      filterOptions: filter.filterOptions.map((option) => ({
        ...option,
        // Disable if it's the last selected option OR if the option doesn't exist in the raw data
        disabled: isLastSelectedOption(option.id) || !existingValues.has(option.id),
      })),
    };
  });
};

export const isFilterSelected = (
  allFiltersValues: FiltersValues,
  currentFiltersValues: FiltersValues,
  currentFiltersData: FilterData[],
  filterLabel: string
) => {
  const allValues = allFiltersValues[filterLabel] || [];
  const currentValues = currentFiltersValues[filterLabel] || [];
  const selectedValues = allValues.filter((value: string) => !currentValues.includes(value));
  if (!selectedValues || selectedValues.length === 0) {
    return false;
  } else {
    const filterData = currentFiltersData.find((item) => item.filterLabel === filterLabel);
    const filterOptions = filterData?.filterOptions;
    return filterOptions
      ? filterOptions.filter((option) => selectedValues.includes(option.id)).some((option) => !option.disabled)
      : false;
  }
};

export const isInitialState = (initialFiltersValues: FiltersValues, currentFiltersValues: FiltersValues) => {
  return Object.entries(initialFiltersValues).every(([filterLabel, initialValues]) => {
    const currentValues = currentFiltersValues[filterLabel] || [];
    return initialValues.every((item) => currentValues.includes(item));
  });
};
